<template>
  <b-modal
    id="plan-steps-import-modal"
    ref="modalRef"
    size="lg"
    no-close-on-backdrop
    :title="$t('Import Maintenance Plan Steps')"
    cancel-variant="outline-secondary"
    hide-footer
    @hidden="resetModal"
  >
    <!-- Template Download & Guidelines Banner -->
    <b-card
      no-body
      class="border-primary bg-light-primary p-1 mb-1 shadow-none"
    >
      <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
        <div class="d-flex align-items-center mb-50 mb-md-0 mr-md-1">
          <feather-icon
            icon="HelpCircleIcon"
            size="22"
            class="text-primary mr-75 flex-shrink-0"
          />
          <div>
            <h6 class="text-primary mb-25 font-weight-bolder">
              {{ $t('Download standard spreadsheet template') }}
            </h6>
            <small class="text-muted d-block mb-25">
              {{ $t('Do not change or remove original headers to ensure correct column recognition.') }}
            </small>
            <small class="text-primary font-weight-bold d-block">
              ℹ️ {{ $t('It is not necessary to save the plan for steps to be saved. Once imported, they are automatically linked to the plan.') }}
            </small>
          </div>
        </div>

        <b-button
          variant="primary"
          size="sm"
          class="flex-shrink-0"
          :disabled="isDownloading"
          @click="downloadTemplate"
        >
          <b-spinner
            v-if="isDownloading"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="DownloadIcon"
            size="14"
            class="mr-50"
          />
          {{ isDownloading ? $t('Downloading...') : $t('Download template') }}
        </b-button>
      </div>
    </b-card>

    <!-- Initial Upload View (when not showing final result or when re-uploading) -->
    <div v-if="!result">
      <!-- General Error Alert -->
      <b-alert
        v-if="generalError"
        variant="danger"
        show
        dismissible
        class="mb-1"
        @dismissed="generalError = ''"
      >
        <div class="alert-body d-flex align-items-center">
          <feather-icon
            icon="AlertCircleIcon"
            class="mr-50 flex-shrink-0"
          />
          <span>{{ generalError }}</span>
        </div>
      </b-alert>

      <!-- Drag and Drop Zone -->
      <div
        class="dropzone-area text-center rounded p-2 d-flex flex-column align-items-center justify-content-center"
        :class="{ 'dropzone-active': isDragging, 'has-file': Boolean(selectedFile) }"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        @click="triggerFileInput"
      >
        <feather-icon
          icon="UploadCloudIcon"
          size="38"
          class="text-primary mb-75"
        />

        <div v-if="!selectedFile">
          <h6 class="mb-25 font-weight-bolder">
            {{ $t('Drag and drop Excel file here or click to browse') }}
          </h6>
          <small class="text-muted">
            {{ $t('Accepted format: .xlsx (Excel)') }}
          </small>
        </div>

        <div
          v-else
          class="file-preview-box d-flex align-items-center p-75 rounded bg-white border"
          @click.stop
        >
          <feather-icon
            icon="FileTextIcon"
            size="24"
            class="text-success mr-75"
          />
          <div class="text-left mr-1">
            <h6 class="mb-0 text-truncate file-name">
              {{ selectedFile.name }}
            </h6>
            <small class="text-muted">{{ formatFileSize(selectedFile.size) }}</small>
          </div>
          <b-button
            variant="flat-danger"
            size="sm"
            class="btn-icon p-25"
            :title="$t('Remove file')"
            @click.stop="removeFile"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
          </b-button>
        </div>

        <input
          ref="fileInputRef"
          type="file"
          class="d-none"
          accept=".xlsx"
          @change="onFileSelected"
        >
      </div>

      <!-- Footer Buttons -->
      <div class="d-flex justify-content-end align-items-center mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="isUploading"
          @click="hide"
        >
          {{ $t('Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="!selectedFile || isUploading"
          @click="uploadFile"
        >
          <b-spinner
            v-if="isUploading"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="UploadIcon"
            size="14"
            class="mr-50"
          />
          {{ isUploading ? $t('Importing steps...') : $t('Import steps') }}
        </b-button>
      </div>
    </div>

    <!-- Result / Feedback View -->
    <div
      v-else
      class="import-result-view"
    >
      <!-- Case 1: Full Success (all valid) -->
      <div
        v-if="result.success && result.skipped === 0"
        class="text-center py-2"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="48"
          class="text-success mb-1"
        />
        <h5 class="text-success font-weight-bolder mb-50">
          {{ $t('Steps imported successfully!') }}
        </h5>
        <p class="text-muted mb-50">
          {{ $t('Steps were added to the maintenance plan.') }}
        </p>
        <p class="text-primary small font-weight-bold mb-1">
          ℹ️ {{ $t('The imported steps are already saved and linked to the plan. You do not need to save the plan again.') }}
        </p>
        <b-button
          variant="primary"
          @click="hide"
        >
          {{ $t('Done') }}
        </b-button>
      </div>

      <!-- Case 2: Partial Success or Total Rejection -->
      <div v-else>
        <!-- Banner Alert -->
        <b-alert
          :variant="result.inserted > 0 ? 'warning' : 'danger'"
          show
          class="mb-1"
        >
          <div class="alert-body d-flex align-items-center">
            <feather-icon
              :icon="result.inserted > 0 ? 'AlertTriangleIcon' : 'AlertCircleIcon'"
              size="20"
              class="mr-50 flex-shrink-0"
            />
            <div>
              <h6
                class="font-weight-bolder mb-25"
                :class="result.inserted > 0 ? 'text-warning' : 'text-danger'"
              >
                {{ result.inserted > 0 ? $t('Import completed with warnings') : $t('Import failed') }}
              </h6>
              <span class="small d-block">
                {{
                  result.inserted > 0
                    ? $t('{inserted} steps added, {skipped} steps skipped.', { inserted: result.inserted, skipped: result.skipped })
                    : $t('No steps were added. Check the rejection reasons below.')
                }}
              </span>
              <span
                v-if="result.inserted > 0"
                class="small font-weight-bold text-warning d-block mt-25"
              >
                ℹ️ {{ $t('The imported steps are already saved and linked to the plan. You do not need to save the plan again.') }}
              </span>
            </div>
          </div>
        </b-alert>

        <!-- Summary Metrics -->
        <div class="d-flex flex-wrap align-items-center mb-1">
          <b-badge
            variant="light-success"
            class="mr-50 py-50 px-75 font-medium-1 mb-50"
          >
            <feather-icon
              icon="CheckIcon"
              size="14"
              class="mr-25"
            />
            {{ $t('Inserted') }}: <strong>{{ result.inserted }}</strong>
          </b-badge>
          <b-badge
            variant="light-danger"
            class="py-50 px-75 font-medium-1 mb-50"
          >
            <feather-icon
              icon="XIcon"
              size="14"
              class="mr-25"
            />
            {{ $t('Skipped / Rejected') }}: <strong>{{ result.skipped }}</strong>
          </b-badge>
        </div>

        <!-- General Errors list if any -->
        <div
          v-if="result.errors && result.errors.length"
          class="mb-1"
        >
          <h6 class="font-weight-bolder text-muted mb-50 small text-uppercase">
            {{ $t('Messages & Errors') }}:
          </h6>
          <b-card
            no-body
            class="bg-light p-75 mb-0 border shadow-none"
          >
            <ul class="mb-0 pl-1 text-danger small font-weight-medium">
              <li
                v-for="(err, idx) in result.errors"
                :key="`err-${idx}`"
              >
                {{ err }}
              </li>
            </ul>
          </b-card>
        </div>

        <!-- Skipped Steps Table -->
        <div
          v-if="result.skippedSteps && result.skippedSteps.length"
          class="skipped-steps-box mb-1"
        >
          <h6 class="font-weight-bolder text-muted mb-50 small text-uppercase">
            {{ $t('Skipped Steps Detail') }}:
          </h6>
          <div class="table-responsive rounded border">
            <table class="table table-sm table-hover mb-0 bg-white">
              <thead class="thead-light">
                <tr>
                  <th style="width: 70px;">
                    {{ $t('Row') }}
                  </th>
                  <th>{{ $t('Step Name') }}</th>
                  <th style="width: 140px;">
                    {{ $t('Interval') }}
                  </th>
                  <th>{{ $t('Reason') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(step, idx) in result.skippedSteps"
                  :key="`skipped-${idx}`"
                >
                  <td>
                    <b-badge variant="light-secondary">
                      #{{ step.row }}
                    </b-badge>
                  </td>
                  <td class="font-weight-bold">
                    {{ step.name || '—' }}
                  </td>
                  <td>
                    <span
                      v-if="step.hourmeter"
                      class="badge badge-light-primary mr-25"
                    >
                      {{ step.hourmeter }}h
                    </span>
                    <span
                      v-if="step.odometer"
                      class="badge badge-light-info"
                    >
                      {{ step.odometer }}km
                    </span>
                    <span
                      v-if="!step.hourmeter && !step.odometer"
                      class="text-muted small"
                    >
                      —
                    </span>
                  </td>
                  <td>
                    <span class="text-danger small font-weight-medium">
                      {{ step.reason }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Action Footer for Result View -->
        <div class="d-flex justify-content-end align-items-center mt-1 pt-50 border-top">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            size="sm"
            @click="tryAnotherFile"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="mr-25"
            />
            {{ $t('Import another file') }}
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="hide"
          >
            {{ $t('Close') }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { ref } from '@vue/composition-api'
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const MODULE = 'app-management'

export default {
  name: 'PlanStepsImportModal',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BModal,
    BSpinner,
  },
  props: {
    planId: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props, { emit, root }) {
    const modalRef = ref(null)
    const fileInputRef = ref(null)

    const selectedFile = ref(null)
    const isDragging = ref(false)
    const isUploading = ref(false)
    const isDownloading = ref(false)
    const result = ref(null)
    const generalError = ref('')

    const show = () => {
      if (modalRef.value) {
        modalRef.value.show()
      }
    }

    const hide = () => {
      if (modalRef.value) {
        modalRef.value.hide()
      }
    }

    const formatFileSize = bytes => {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${parseFloat((bytes / (k ** i)).toFixed(1))} ${sizes[i]}`
    }

    const validateAndSetFile = file => {
      generalError.value = ''
      if (!file) return

      const fileName = file.name || ''
      if (!fileName.toLowerCase().endsWith('.xlsx')) {
        generalError.value = root.$t('Invalid file format. Please select an Excel file (.xlsx).')
        return
      }

      selectedFile.value = file
    }

    const onDragOver = () => {
      isDragging.value = true
    }

    const onDragLeave = () => {
      isDragging.value = false
    }

    const onDrop = event => {
      isDragging.value = false
      if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        validateAndSetFile(event.dataTransfer.files[0])
      }
    }

    const triggerFileInput = () => {
      if (fileInputRef.value) {
        fileInputRef.value.click()
      }
    }

    const onFileSelected = event => {
      if (event.target && event.target.files && event.target.files.length > 0) {
        validateAndSetFile(event.target.files[0])
      }
    }

    const removeFile = () => {
      selectedFile.value = null
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
      }
    }

    const resetModal = () => {
      selectedFile.value = null
      isDragging.value = false
      isUploading.value = false
      isDownloading.value = false
      result.value = null
      generalError.value = ''
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
      }
    }

    const tryAnotherFile = () => {
      resetModal()
    }

    const downloadTemplate = () => {
      isDownloading.value = true
      store.dispatch(`${MODULE}/downloadMaintenancePlanStepsModel`)
        .then(response => {
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'ImportPreventiveMaintenancePlanStepsModel.xlsx')
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          root.$toast({
            component: ToastificationContent,
            props: {
              title: root.$t('Download completed'),
              icon: 'DownloadIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          root.$toast({
            component: ToastificationContent,
            props: {
              title: root.$t('Error downloading template model'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isDownloading.value = false
        })
    }

    const uploadFile = () => {
      if (!selectedFile.value || !props.planId) return

      isUploading.value = true
      generalError.value = ''

      store.dispatch(`${MODULE}/uploadMaintenancePlanSteps`, {
        preventiveMaintenancePlanId: props.planId,
        file: selectedFile.value,
      })
        .then(response => {
          const data = response.data || {}
          result.value = {
            success: Boolean(data.success),
            inserted: Number(data.inserted) || 0,
            skipped: Number(data.skipped) || 0,
            errors: Array.isArray(data.errors) ? data.errors : [],
            skippedSteps: Array.isArray(data.skippedSteps) ? data.skippedSteps : [],
          }

          if (result.value.inserted > 0) {
            emit('imported', result.value)
          }

          if (result.value.success && result.value.skipped === 0) {
            root.$toast({
              component: ToastificationContent,
              props: {
                title: root.$t('Steps imported successfully!'),
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
          }
        })
        .catch(error => {
          const resData = error.response && error.response.data
          if (resData && typeof resData === 'object' && (resData.errors || resData.skippedSteps)) {
            result.value = {
              success: Boolean(resData.success),
              inserted: Number(resData.inserted) || 0,
              skipped: Number(resData.skipped) || 0,
              errors: Array.isArray(resData.errors) ? resData.errors : [],
              skippedSteps: Array.isArray(resData.skippedSteps) ? resData.skippedSteps : [],
            }
          } else {
            generalError.value = (resData && resData.message)
              || root.$t('Unexpected error importing steps. Please check file format and try again.')
          }
        })
        .finally(() => {
          isUploading.value = false
        })
    }

    return {
      modalRef,
      fileInputRef,
      selectedFile,
      isDragging,
      isUploading,
      isDownloading,
      result,
      generalError,
      show,
      hide,
      formatFileSize,
      onDragOver,
      onDragLeave,
      onDrop,
      triggerFileInput,
      onFileSelected,
      removeFile,
      resetModal,
      tryAnotherFile,
      downloadTemplate,
      uploadFile,
    }
  },
}
</script>

<style lang="scss" scoped>
.dropzone-area {
  border: 2px dashed #7367f0;
  background-color: #f8f8f8;
  cursor: pointer;
  transition: all 0.25s ease-in-out;
  min-height: 180px;

  &:hover,
  &.dropzone-active {
    border-color: #5e50ee;
    background-color: #f3f2ff;
  }

  &.has-file {
    border-color: #28c76f;
    background-color: #f4fbf7;
  }
}

.file-preview-box {
  max-width: 90%;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.05);

  .file-name {
    max-width: 260px;
    font-size: 0.95rem;
    font-weight: 600;
  }
}

.skipped-steps-box {
  max-height: 240px;
  overflow-y: auto;
}
</style>
